import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import PasswordConfirmDialog from "../components/PasswordConfirmDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Filter, Wallet, HandCoins, X, Package, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function SalesHistory() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", customer: "", payment_type: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.customer) params.customer = filters.customer;
      if (filters.payment_type && filters.payment_type !== "all") params.payment_type = filters.payment_type;
      const { data } = await api.get("/sales", { params });
      setSales(data || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const [deleteSaleId, setDeleteSaleId] = useState(null);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const totalRevenue = sales.reduce((a, s) => a + s.sale_price * s.quantity, 0);
  const totalProfit = sales.reduce((a, s) => a + (s.sale_price - s.purchase_price) * s.quantity, 0);
  const totalFiadoDue = sales.reduce((a, s) => a + (s.amount_due || 0), 0);

  const clearFilters = () => setFilters({ date_from: "", date_to: "", customer: "", payment_type: "all" });

  return (
    <div className="min-h-screen bg-background" data-testid="sales-history-page">
      <DashboardHeader title="Histórico de vendas" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Vendas</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Histórico</h1>
          <p className="text-sm text-muted-foreground mt-1">{sales.length} venda(s) · Receita: {format(totalRevenue)} · Lucro: {format(totalProfit)}</p>
        </div>

        {/* Filters */}
        <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-sm font-semibold"><Filter className="w-4 h-4" /> Filtros</div>
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn"><X className="w-4 h-4 mr-1" /> Limpar</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cliente</Label>
              <Input value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })} placeholder="Ex: Maria" data-testid="filter-customer" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Pagamento</Label>
              <Select value={filters.payment_type} onValueChange={(v) => setFilters({ ...filters, payment_type: v })}>
                <SelectTrigger data-testid="filter-payment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="fiado">Fiado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Sales list */}
        {loading ? (
          <div className="space-y-3">{Array.from({length: 4}).map((_, i) => <div key={i} className="h-20 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : sales.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card" data-testid="empty-sales">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground">Nenhuma venda encontrada com os filtros atuais.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sales.map((s) => (
              <div key={s.id} className="bg-white rounded-xl lb-shadow-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sale-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="lb-overline">{new Date(s.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="font-semibold truncate">{s.product_name} <span className="text-muted-foreground font-normal">· {s.variant_name}</span></div>
                  {s.customer_name && <div className="text-xs text-muted-foreground mt-0.5">Cliente: {s.customer_name}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full ${s.payment_type === "dinheiro" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "bg-[hsl(var(--accent))]/15 text-[#8a5a10]"}`}>
                    {s.payment_type === "dinheiro" ? <Wallet className="w-3 h-3" /> : <HandCoins className="w-3 h-3" />}
                    {s.payment_type}
                  </span>
                  <div className="text-right">
                    <div className="font-display text-lg">{format(s.sale_price * s.quantity)}</div>
                    {s.amount_due > 0 && (
                      <div className="text-xs text-[hsl(var(--destructive))] font-semibold">Em dívida: {format(s.amount_due)}</div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteSaleId(s.id)} data-testid={`delete-sale-${s.id}`} title="Eliminar venda (devolução)">
                    <Trash2 className="w-4 h-4 text-muted-foreground hover:text-[hsl(var(--destructive))]" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalFiadoDue > 0 && (
          <div className="text-right text-sm text-muted-foreground">Total em dívida nestas vendas: <span className="font-semibold text-[hsl(var(--destructive))]">{format(totalFiadoDue)}</span></div>
        )}
      </main>

      <PasswordConfirmDialog
        open={!!deleteSaleId}
        onOpenChange={(o) => !o && setDeleteSaleId(null)}
        title="Eliminar venda"
        description="Esta ação repõe o stock e reverte a alocação FIFO. Usar para devoluções ou registos por engano."
        actionLabel="Eliminar venda"
        onConfirm={async (password) => {
          await api.delete(`/sales/${deleteSaleId}`, { headers: { "X-Confirm-Password": password } });
          toast.success("Venda eliminada · stock reposto");
          setDeleteSaleId(null);
          load();
        }}
      />
    </div>
  );
}
